import type {Dispatch, GetState, ThunkAction} from 'src/reducers';

import type {AudienceList} from 'src/types/audience-list';
import type {TalentPoolHierarchy} from 'src/types/talent-pools';
import type {AutomationWorkflowAudienceState} from 'src/reducers/automation-workflow-audience';

import {normalizeAudienceBlocks} from 'src/utils/audience-list';
import {getListsById} from 'src/action-creators/audience-lists';
import {getAutomationWorkflow} from 'src/action-creators/automation-workflow';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-bff';


export type AudienceFormSteps =
  | 'createMode'
  | 'details'
  | 'copyLists'
  | 'rules'
  | 'review'
  | 'pickNLink'
  | 'summary';
export type UpdateStatePayload = Partial<AutomationWorkflowAudienceState>;

export const UPDATE_STATE = 'automation-workflow-audience/update';
export const RECEIVE_SELECTED_LISTS =
  'automation-workflow-audience/receive-selected-lists';
export const SET_INCLUDED_LISTS =
  'automation-workflow-audience/set-included-lists';
export const SET_EXCLUDED_LISTS =
  'automation-workflow-audience/set-excluded-lists';
export const SET_TALENT_POOL_INCLUDED_LISTS =
  'automation-workflow-audience/set-talent-pool-included-lists';
export const SET_TALENT_POOL_EXCLUDED_LISTS =
  'automation-workflow-audience/set-talent-pool-excluded-lists';
export const RESET = 'automation-workflow-audience/reset';
export const GOTO_STEP = 'automation-workflow-audience/goto-step';

type UpdateStateAction = {
  type: 'automation-workflow-audience/update';
  payload: UpdateStatePayload;
};
type ReceiveSelectedListsAction = {
  type: 'automation-workflow-audience/receive-selected-lists';
  payload: {
    includedLists: Map<string, Partial<AudienceList>>;
    excludedLists: Map<string, Partial<AudienceList>>;
  };
};
type SetIncludedListsAction = {
  type: 'automation-workflow-audience/set-included-lists';
  payload: Map<string, Partial<AudienceList>>;
};
type SetExcludedListsAction = {
  type: 'automation-workflow-audience/set-excluded-lists';
  payload: Map<string, Partial<AudienceList>>;
};
type SetTalentPoolIncludedListsAction = {
  type: 'automation-workflow-audience/set-talent-pool-included-lists';
  payload: Map<string, Partial<TalentPoolHierarchy>>;
};
type SetTalentPoolExcludedListsAction = {
  type: 'automation-workflow-audience/set-talent-pool-excluded-lists';
  payload: Map<string, Partial<TalentPoolHierarchy>>;
};
type ResetStateAction = {
  type: 'automation-workflow-audience/reset';
};

export type AutomationWorkflowAudienceAction =
  | UpdateStateAction
  | ReceiveSelectedListsAction
  | SetIncludedListsAction
  | SetExcludedListsAction
  | SetTalentPoolIncludedListsAction
  | SetTalentPoolExcludedListsAction
  | ResetStateAction;

export const updateState = (
  payload: UpdateStatePayload,
): UpdateStateAction => ({
  type: UPDATE_STATE,
  payload,
});

export const receiveSelectedLists = (payload: {
  includedLists: Map<string, Partial<AudienceList>>;
  excludedLists: Map<string, Partial<AudienceList>>;
}): ReceiveSelectedListsAction => ({
  type: RECEIVE_SELECTED_LISTS,
  payload,
});
export const setIncludedLists = (
  payload: Map<string, Partial<AudienceList>>,
): SetIncludedListsAction => ({
  type: SET_INCLUDED_LISTS,
  payload,
});
export const setExcludedLists = (
  payload: Map<string, Partial<AudienceList>>,
): SetExcludedListsAction => ({
  type: SET_EXCLUDED_LISTS,
  payload,
});
export const setTalentPoolIncludedLists = (
  payload: Map<string, Partial<TalentPoolHierarchy>>,
): SetTalentPoolIncludedListsAction => ({
  type: SET_TALENT_POOL_INCLUDED_LISTS,
  payload,
});
export const setTalentPoolExcludedLists = (
  payload: Map<string, Partial<TalentPoolHierarchy>>,
): SetTalentPoolExcludedListsAction => ({
  type: SET_TALENT_POOL_EXCLUDED_LISTS,
  payload,
});

export const reset = (): ResetStateAction => ({type: RESET});
export const linkAudienceListsToAutomationWorkflow: (
  journeyId: string,
) => ThunkAction<unknown> =
  (automationWorkflowId) => async (dispatch: Dispatch, getState: GetState) => {
    const includedListIds = [
      ...getState().automationWorkflowAudience.includedLists.keys(),
    ];
    const excludedListIds = [
      ...getState().automationWorkflowAudience.excludedLists.keys(),
    ];
    await dispatch(
      reduxApi.put(`workflow/${automationWorkflowId}/mappings`, {
        included_filter_ids: includedListIds,
        excluded_filter_ids: excludedListIds,
      }),
    );
    //also update the automation workflow state
    dispatch(getAutomationWorkflow(automationWorkflowId));
    return {includedListIds, excludedListIds};
  };

export const linkTalentPoolToAutomationWorkflow =
  (automationWorkflowId: string): ThunkAction<unknown> =>
  async (dispatch, getState) => {
    const state = getState();
    const includedLists =
      state.automationWorkflowAudience.talentPoolIncludedLists;
    const excludedLists =
      state.automationWorkflowAudience.talentPoolExcludedLists;

    const extractChildFilterIds = (
      listsMap: Map<string, Partial<TalentPoolHierarchy>>,
    ): number[] => {
      const ids: number[] = [];
      for (const list of listsMap.values()) {
        // @ts-expect-error - TS18048 - 'list.child_folders' is possibly 'undefined'.
        for (const folder of list.child_folders) {
          ids.push(folder.list_filter_id);
        }
      }
      return ids;
    };

    const includedListIds = extractChildFilterIds(includedLists);
    const excludedListIds = extractChildFilterIds(excludedLists);

    await dispatch(
      reduxApi.put(`workflow/${automationWorkflowId}/mappings`, {
        included_filter_ids: includedListIds,
        excluded_filter_ids: excludedListIds,
      }),
    );

    dispatch(getAutomationWorkflow(automationWorkflowId));

    return {includedListIds, excludedListIds};
  };

export const updateSelectedLists: (listDetails: {
  includedLists: Map<string, Partial<AudienceList>>;
  excludedLists: Map<string, Partial<AudienceList>>;
}) => ReceiveSelectedListsAction = (listDetails) =>
  receiveSelectedLists(listDetails);

export const getAutomationWorkflowAudience: (
  automationWorkflowId: string,
) => ThunkAction<unknown> = flow(
  key(
    (automationWorkflowId) =>
      `automationWorkflowAudience/get-${automationWorkflowId}`,
  ),
  fetching(),
)((automationWorkflowId) => async (dispatch: Dispatch) => {
  const response = await dispatch(
    reduxApi.get(`/workflow/${automationWorkflowId}/mappings`),
  );

  const listIds = [...response.included, ...response.excluded];
  let filters: Array<never> = [];
  if (listIds.length > 0) {
    const listsDetailResponse = await dispatch(getListsById(listIds));
    if (listsDetailResponse) {
      // @ts-expect-error TS2339
      filters = listsDetailResponse.filters;
    }
  }
  // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
  const listDetailsMap = new Map(filters.map((list) => [list.id, list]));
  const includedLists = new Map(
    // @ts-expect-error - TS7006 - Parameter 'listId' implicitly has an 'any' type.
    response.included.map((listId) => [listId, listDetailsMap.get(listId)]),
  );

  const excludedLists = new Map(
    // @ts-expect-error - TS7006 - Parameter 'listId' implicitly has an 'any' type.
    response.excluded.map((listId) => [listId, listDetailsMap.get(listId)]),
  );
  // @ts-expect-error - TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<string, Partial<AudienceList>>'. | TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<string, Partial<AudienceList>>'.
  dispatch(receiveSelectedLists({includedLists, excludedLists}));
  return response;
});

export const createAudienceListAndLink: (
  automationWorkflowId: string,
) => ThunkAction<unknown> =
  (automationWorkflowId) => (dispatch: Dispatch, getState: GetState) => {
    const audienceList = getState().audienceList;
    const {name, entityType, inclusions, exclusions, all} = audienceList;
    //@ts-expect-error
    const rule = normalizeAudienceBlocks({inclusions, exclusions, all});
    //create
    const apiPayload = {
      name,
      rule,
      entityType,
      source: 'LIST_UI',
    } as const;

    return dispatch(
      reduxApi.post(
        `/workflow/${automationWorkflowId}/list-create-and-map`,
        apiPayload,
      ),
    ).then(() => dispatch(getAutomationWorkflow(automationWorkflowId)));
  };
